import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../store/store';
import { logout } from '../../store/slices/authSlice';
import { addNotification } from '../../store/slices/uiSlice';
import { notificationService, Notification } from '../../services/NotificationService';
import { getValidToken } from '../../utils/auth';
import './UserMenu.css';
import ProfileImage from '../../assets/profile.png';
import WithdrawalModal from '../modals/WithdrawalModal';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const UserMenu: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [isWithdrawalModalOpen, setIsWithdrawalModalOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isNotificationClosing, setIsNotificationClosing] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const notificationRef = useRef<HTMLDivElement>(null);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [notificationPage, setNotificationPage] = useState(1);
  const NOTIFICATIONS_PER_PAGE = 10;

  // Subscribe to notifications and load from API
  useEffect(() => {
    const unsubscribe = notificationService.subscribe(setNotifications);
    
    // Load notifications from API - only if user is logged in
    const loadNotifications = async () => {
      // Check both Redux state AND localStorage token
      const token = getValidToken();
      if (!token || !user) {
        return; // Don't load notifications if not logged in
      }
      
      try {
        const response = await fetch(`${API_BASE_URL}/api/notifications`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.status === 401) {
          return; // Don't log error, just skip
        }
        
        if (response.ok) {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const data = await response.json();
            const loadedNotifications: Notification[] = data.map((notif: any) => ({
              id: notif.id,
              type: notif.type,
              title: notif.title,
              message: notif.message,
              amount: notif.amount ? Number(notif.amount) / 1000000000 : undefined,
              timestamp: new Date(notif.createdAt),
              read: notif.isRead
            }));
            
            notificationService.loadNotifications(loadedNotifications);
          }
        }
      } catch (error) {
        // Silently fail - don't spam console
      }
    };

    if (user) {
      loadNotifications();
    }
    
    return unsubscribe;
  }, [user]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        handleCloseDropdown();
      }
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        handleCloseNotifications();
      }
    };

    if (isDropdownOpen || isNotificationOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen, isNotificationOpen]);

  const handleCloseDropdown = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsDropdownOpen(false);
      setIsClosing(false);
    }, 200);
  };

  const handleCloseNotifications = () => {
    setIsNotificationClosing(true);
    setTimeout(() => {
      setIsNotificationOpen(false);
      setIsNotificationClosing(false);
    }, 200);
  };

  const handleMarkAllAsRead = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/notifications/read-all`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${getValidToken()}`
        }
      });
      
      if (response.ok) {
        notificationService.markAllAsRead();
      }
    } catch (error) {
      console.error('Failed to mark all as read:', error);
      // Still update locally even if API call fails
      notificationService.markAllAsRead();
    }
  };

  const getUnreadCount = () => {
    return notifications.filter(n => !n.read).length;
  };

  const formatTimeAgo = (date: Date) => {
    const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
    
    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  const handleDisconnect = async () => {
    try {
      dispatch(logout());
      dispatch(addNotification({
        type: 'info',
        title: 'Logged Out',
        message: 'You have been logged out successfully',
      }));
      handleCloseDropdown();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleSettingsClick = () => {
    navigate('/settings');
    handleCloseDropdown();
  };

  const handleTransactionsClick = () => {
    navigate('/settings?tab=transactions');
    handleCloseDropdown();
  };

  const handleWithdrawalClick = () => {
    setIsWithdrawalModalOpen(true);
    handleCloseDropdown();
  };

  const handleAdminClick = () => {
    navigate('/moneymandavetj');
    handleCloseDropdown();
  };

  if (!user) return null;

  const getProfileImageSrc = () => {
    if (user.profilePicture) {
      // If it's already a full URL, use it as is
      if (user.profilePicture.startsWith('http')) {
        return user.profilePicture;
      }
      // Otherwise, construct the API URL with the backend URL
      return `${API_BASE_URL}/api/profile/picture/${user.profilePicture}`;
    }
    return ProfileImage;
  };

  return (
    <div className="user-menu">
      <div className="user-menu-items">
        {/* Notification Bell */}
        <div className="notification-container" ref={notificationRef}>
          <button 
            className="notification-btn"
            onClick={() => {
              if (isNotificationOpen) {
                handleCloseNotifications();
              } else {
                setIsNotificationOpen(true);
              }
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
              <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
            </svg>
            {getUnreadCount() > 0 && (
              <span className="notification-badge">{getUnreadCount()}</span>
            )}
          </button>

          {/* Notification Dropdown */}
          {isNotificationOpen && (
            <div className={`notification-dropdown ${isNotificationClosing ? 'closing' : ''}`}>
              <div className="notification-header">
                <h3>Notifications</h3>
                {getUnreadCount() > 0 && (
                  <button className="mark-all-read" onClick={handleMarkAllAsRead}>
                    Mark all read
                  </button>
                )}
              </div>
              <div className="notification-list">
                {notifications.length === 0 ? (
                  <div className="notification-empty">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                      <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                    </svg>
                    <p>No notifications yet</p>
                  </div>
                ) : (
                  <>
                    {notifications.slice(0, notificationPage * NOTIFICATIONS_PER_PAGE).map((notif) => (
                      <div 
                        key={notif.id} 
                        className={`notification-item ${!notif.read ? 'unread' : ''}`}
                        onClick={() => notificationService.markAsRead(notif.id)}
                      >
                        <div className="notification-content-wrapper">
                          <div className="notification-icon">
                            {notif.type === 'deposit' && (
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                              </svg>
                            )}
                            {notif.type === 'withdrawal' && (
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M12 19V5M5 12l7-7 7 7"/>
                              </svg>
                            )}
                          </div>
                          <div className="notification-content">
                            <div className="notification-title">{notif.title}</div>
                            <div className="notification-message">{notif.message}</div>
                            <div className="notification-time">{formatTimeAgo(notif.timestamp)}</div>
                          </div>
                          {!notif.read && <div className="unread-dot"></div>}
                        </div>
                      </div>
                    ))}
                    {notifications.length > notificationPage * NOTIFICATIONS_PER_PAGE && (
                      <button 
                        className="load-more-notifications"
                        onClick={() => setNotificationPage(prev => prev + 1)}
                      >
                        Load More
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Profile Button */}
        <div ref={dropdownRef}>
          <button 
            className="profile-btn"
            onClick={() => {
              if (isDropdownOpen) {
                handleCloseDropdown();
              } else {
                setIsDropdownOpen(true);
              }
            }}
          >
          <img 
            src={getProfileImageSrc()} 
            alt="Profile" 
            className="profile-image"
            onError={(e) => {
              // Fallback to default profile image if loading fails
              e.currentTarget.src = ProfileImage;
            }}
          />
        </button>

        {/* Profile Dropdown Menu */}
        {isDropdownOpen && (
          <div className={`profile-dropdown ${isClosing ? 'closing' : ''}`}>
            <div className="dropdown-menu-items">
              <button className="dropdown-menu-item" onClick={handleSettingsClick}>
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" strokeWidth="0"></g><g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g><g id="SVGRepo_iconCarrier"><path fillRule="evenodd" clipRule="evenodd" d="M18.51 9.49h1.87c.88 0 1.6.72 1.6 1.6v1.8c0 .88-.72 1.6-1.6 1.6h-1.87c-.03.092-.068.179-.106.269l-.034.081 1.32 1.32c.62.62.62 1.64 0 2.26l-1.27 1.27c-.62.62-1.64.62-2.26 0l-1.32-1.32c-.064.029-.131.055-.2.08l-.15.06v1.87c0 .88-.72 1.6-1.6 1.6h-1.8c-.88 0-1.6-.72-1.6-1.6v-1.87c-.092-.03-.179-.068-.269-.106l-.081-.034-1.32 1.32c-.62.62-1.64.62-2.26 0l-1.27-1.27c-.62-.62-.62-1.64 0-2.26l1.32-1.32c-.029-.064-.055-.131-.08-.2l-.06-.15H3.6c-.88 0-1.6-.72-1.6-1.6v-1.8c0-.88.72-1.6 1.6-1.6h1.87c.03-.092.068-.179.106-.269l.034-.081-1.32-1.32c-.62-.62-.62-1.64 0-2.26l1.27-1.27c.62-.62 1.64-.62 2.26 0l1.32 1.32c.064-.029.131-.055.2-.08l.15-.06V3.6c0-.88.72-1.6 1.6-1.6h1.8c.88 0 1.6.72 1.6 1.6v1.87c.092.03.179.068.269.106l.081.034 1.32-1.32c.62-.62 1.64-.62 2.26 0l1.27 1.27c.62.62.62 1.64 0 2.26l-1.32 1.32c.029.064.055.131.08.2l.06.15ZM16 12a4 4 0 1 1-8 0 4 4 0 0 1 8 0Z" fill="#ffffff"></path></g></svg>
                Settings
              </button>

              <button className="dropdown-menu-item" onClick={handleTransactionsClick}>
                <svg xmlns="http://www.w3.org/2000/svg" fill="#ffffff" viewBox="0 0 24 24" stroke="#ffffff"><g id="SVGRepo_bgCarrier" strokeWidth="0"></g><g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g><g id="SVGRepo_iconCarrier"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1.41 16.09V20h-2.67v-1.93c-1.71-.36-3.16-1.46-3.27-3.4h1.96c.1 1.05.82 1.87 2.65 1.87 1.96 0 2.4-.98 2.4-1.59 0-.83-.44-1.61-2.67-2.14-2.48-.6-4.18-1.62-4.18-3.67 0-1.72 1.39-2.84 3.11-3.21V4h2.67v1.95c1.86.45 2.79 1.86 2.85 3.39H14.3c-.05-1.11-.64-1.87-2.22-1.87-1.5 0-2.4.68-2.4 1.64 0 .84.65 1.39 2.67 1.91s4.18 1.39 4.18 3.91c-.01 1.83-1.38 2.83-3.12 3.16z"></path></g></svg>
                Transactions
              </button>

              <button className="dropdown-menu-item" onClick={handleWithdrawalClick}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 19V5M5 12l7-7 7 7"/>
                </svg>
                Withdraw
              </button>

              {user.isAdmin && (
                <button className="dropdown-menu-item" onClick={handleAdminClick}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"/>
                    <path d="M12 7v5l3 3"/>
                  </svg>
                  Admin
                </button>
              )}

              <button className="dropdown-menu-item disconnect" onClick={handleDisconnect}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                  <polyline points="16 17 21 12 16 7"></polyline>
                  <line x1="21" y1="12" x2="9" y2="12"></line>
                </svg>
                Disconnect
              </button>
            </div>
          </div>
        )}
        </div>
      </div>

      <WithdrawalModal 
        isOpen={isWithdrawalModalOpen} 
        onClose={() => setIsWithdrawalModalOpen(false)} 
      />
    </div>
  );
};

export default UserMenu;
